import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import { airportByLowerCode } from "@/lib/seo-routes";
import { formatDisplayPrice } from "@/lib/price-display";
import { isCountryCode } from "@/lib/airports";

export type PopularRoute = {
  from: string;
  to: string;
  city: string;
  price: string;
};

// Fallback / seed list (used when DB has too little data, or as price source).
// Order here is the default ordering; price is shown as-is.
const FALLBACK: PopularRoute[] = [
  { from: "WAW", to: "BCN", city: "Barcelona", price: "od 289 zł" },
  { from: "WAW", to: "FCO", city: "Rzym", price: "od 319 zł" },
  { from: "KRK", to: "LGW", city: "Londyn", price: "od 199 zł" },
  { from: "WAW", to: "CDG", city: "Paryż", price: "od 259 zł" },
  { from: "WAW", to: "AMS", city: "Amsterdam", price: "od 229 zł" },
  { from: "KRK", to: "MXP", city: "Mediolan", price: "od 209 zł" },
  { from: "WAW", to: "LIS", city: "Lizbona", price: "od 349 zł" },
  { from: "WAW", to: "IST", city: "Stambuł", price: "od 379 zł" },
  { from: "GDN", to: "ATH", city: "Ateny", price: "od 449 zł" },
  { from: "WAW", to: "AGP", city: "Malaga", price: "od 299 zł" },
  { from: "WAW", to: "DXB", city: "Dubaj", price: "od 1 290 zł" },
  { from: "WAW", to: "BKK", city: "Bangkok", price: "od 2 490 zł" },
];

// Dodatkowe ceny seed dla par, które przychodzą z agregacji search_queries.
// Używane wyłącznie jako źródło ceny (nie wpływa na kolejność / wybór tras).
const EXTRA_PRICES: Array<{ from: string; to: string; price: string }> = [
  { from: "WAW", to: "LHR", price: "od 249 zł" },
  { from: "WAW", to: "LGW", price: "od 219 zł" },
  { from: "WAW", to: "STN", price: "od 179 zł" },
  { from: "WRO", to: "LTN", price: "od 169 zł" },
  { from: "WRO", to: "STN", price: "od 169 zł" },
  { from: "WRO", to: "EDI", price: "od 229 zł" },
  { from: "WRO", to: "MLA", price: "od 339 zł" },
  { from: "WRO", to: "VNO", price: "od 199 zł" },
  { from: "WRO", to: "AGP", price: "od 279 zł" },
  { from: "WRO", to: "BCN", price: "od 269 zł" },
  { from: "WRO", to: "FCO", price: "od 299 zł" },
  { from: "WAW", to: "ZAD", price: "od 259 zł" },
  { from: "WAW", to: "SPU", price: "od 269 zł" },
  { from: "WAW", to: "DBV", price: "od 329 zł" },
  { from: "WAW", to: "OPO", price: "od 339 zł" },
  { from: "WAW", to: "TIA", price: "od 289 zł" },
  { from: "WAW", to: "TLV", price: "od 599 zł" },
  { from: "WAW", to: "JFK", price: "od 1 990 zł" },
  { from: "WAW", to: "EWR", price: "od 1 890 zł" },
  { from: "WAW", to: "ORD", price: "od 2 190 zł" },
  { from: "WAW", to: "PEK", price: "od 2 290 zł" },
  { from: "WAW", to: "NRT", price: "od 2 990 zł" },
  { from: "WAW", to: "ICN", price: "od 2 490 zł" },
  { from: "WAW", to: "SIN", price: "od 2 690 zł" },
  { from: "WAW", to: "DEL", price: "od 1 990 zł" },
  { from: "WAW", to: "CMB", price: "od 2 590 zł" },
  { from: "WAW", to: "HKT", price: "od 2 790 zł" },
  { from: "WAW", to: "CUN", price: "od 2 490 zł" },
  { from: "WAW", to: "MIA", price: "od 2 290 zł" },
  { from: "WAW", to: "LAX", price: "od 2 690 zł" },
  { from: "WAW", to: "YYZ", price: "od 2 290 zł" },
  { from: "KRK", to: "STN", price: "od 159 zł" },
  { from: "KRK", to: "LTN", price: "od 169 zł" },
  { from: "KRK", to: "BCN", price: "od 219 zł" },
  { from: "KRK", to: "FCO", price: "od 229 zł" },
  { from: "KRK", to: "AGP", price: "od 269 zł" },
  { from: "KRK", to: "VLC", price: "od 239 zł" },
  { from: "KRK", to: "ALC", price: "od 259 zł" },
  { from: "KRK", to: "DUB", price: "od 199 zł" },
  { from: "KRK", to: "EDI", price: "od 219 zł" },
  { from: "KRK", to: "CIA", price: "od 219 zł" },
  { from: "GDN", to: "STN", price: "od 169 zł" },
  { from: "GDN", to: "LTN", price: "od 179 zł" },
  { from: "GDN", to: "DUB", price: "od 209 zł" },
  { from: "GDN", to: "BGY", price: "od 199 zł" },
  { from: "GDN", to: "AGP", price: "od 289 zł" },
  { from: "GDN", to: "BCN", price: "od 269 zł" },
  { from: "POZ", to: "STN", price: "od 169 zł" },
  { from: "POZ", to: "DUB", price: "od 199 zł" },
  { from: "POZ", to: "EDI", price: "od 209 zł" },
  { from: "KTW", to: "STN", price: "od 169 zł" },
  { from: "KTW", to: "DUB", price: "od 189 zł" },
  { from: "KTW", to: "AGP", price: "od 269 zł" },
  { from: "KTW", to: "BCN", price: "od 249 zł" },
  { from: "WAW", to: "ALC", price: "od 279 zł" },
  { from: "WAW", to: "VLC", price: "od 269 zł" },
  { from: "WAW", to: "PMI", price: "od 299 zł" },
  { from: "WAW", to: "TFS", price: "od 489 zł" },
  { from: "WAW", to: "LPA", price: "od 489 zł" },
  { from: "WAW", to: "FUE", price: "od 499 zł" },
  { from: "WAW", to: "ACE", price: "od 499 zł" },
  { from: "WAW", to: "MAD", price: "od 269 zł" },
  { from: "WAW", to: "BRU", price: "od 239 zł" },
  { from: "WAW", to: "CRL", price: "od 199 zł" },
  { from: "WAW", to: "BER", price: "od 199 zł" },
  { from: "WAW", to: "MUC", price: "od 229 zł" },
  { from: "WAW", to: "FRA", price: "od 239 zł" },
  { from: "WAW", to: "DUS", price: "od 219 zł" },
  { from: "WAW", to: "HAM", price: "od 229 zł" },
  { from: "WAW", to: "CPH", price: "od 219 zł" },
  { from: "WAW", to: "ARN", price: "od 229 zł" },
  { from: "WAW", to: "OSL", price: "od 239 zł" },
  { from: "WAW", to: "HEL", price: "od 249 zł" },
  { from: "WAW", to: "VIE", price: "od 219 zł" },
  { from: "WAW", to: "BUD", price: "od 199 zł" },
  { from: "WAW", to: "PRG", price: "od 199 zł" },
  { from: "WAW", to: "ZRH", price: "od 269 zł" },
  { from: "WAW", to: "GVA", price: "od 269 zł" },
  { from: "WAW", to: "DUB", price: "od 229 zł" },
  { from: "WAW", to: "EDI", price: "od 239 zł" },
  { from: "WAW", to: "MAN", price: "od 229 zł" },
  { from: "WAW", to: "BHX", price: "od 219 zł" },
  { from: "WAW", to: "NCL", price: "od 219 zł" },
  { from: "WAW", to: "ATH", price: "od 369 zł" },
  { from: "WAW", to: "SKG", price: "od 379 zł" },
  { from: "WAW", to: "HER", price: "od 399 zł" },
  { from: "WAW", to: "RHO", price: "od 419 zł" },
  { from: "WAW", to: "CHQ", price: "od 419 zł" },
  { from: "WAW", to: "LCA", price: "od 429 zł" },
  { from: "WAW", to: "SOF", price: "od 269 zł" },
  { from: "WAW", to: "OTP", price: "od 249 zł" },
  { from: "WAW", to: "BEG", price: "od 269 zł" },
  { from: "WAW", to: "TBS", price: "od 489 zł" },
  { from: "WAW", to: "EVN", price: "od 499 zł" },
  { from: "WAW", to: "RIX", price: "od 199 zł" },
  { from: "WAW", to: "TLL", price: "od 219 zł" },
  { from: "WAW", to: "VNO", price: "od 189 zł" },
  { from: "WAW", to: "KEF", price: "od 489 zł" },
  { from: "WAW", to: "MLA", price: "od 329 zł" },
  { from: "WAW", to: "NAP", price: "od 299 zł" },
  { from: "WAW", to: "CTA", price: "od 299 zł" },
  { from: "WAW", to: "PMO", price: "od 309 zł" },
  { from: "WAW", to: "BLQ", price: "od 269 zł" },
  { from: "WAW", to: "VCE", price: "od 259 zł" },
  { from: "WAW", to: "VRN", price: "od 259 zł" },
  { from: "WAW", to: "MXP", price: "od 229 zł" },
  { from: "WAW", to: "BGY", price: "od 199 zł" },
  { from: "SOF", to: "KRK", price: "od 199 zł" },
  { from: "SOF", to: "KTW", price: "od 199 zł" },
  { from: "SOF", to: "WAW", price: "od 219 zł" },
];


const PRICE_BY_PAIR = new Map<string, string>([
  ...FALLBACK.map((r) => [`${r.from}-${r.to}`, r.price] as [string, string]),
  ...EXTRA_PRICES.map((r) => [`${r.from}-${r.to}`, r.price] as [string, string]),
]);

// Threshold: jeśli baza ma mniej niż tyle unikalnych tras z minimum 2 zapytaniami,
// zwracamy statyczny fallback (uniknięcie wyświetlenia 1-2 losowych tras).
const MIN_ROUTES_FROM_DB = 6;
const MIN_COUNT_PER_ROUTE = 2;

export const getPopularRoutes = createServerFn({ method: "GET" }).handler(
  async (): Promise<PopularRoute[]> => {
    try {
      const url = process.env.SUPABASE_URL ?? process.env.VITE_SUPABASE_URL;
      const key =
        process.env.SUPABASE_SERVICE_ROLE_KEY ??
        process.env.SUPABASE_PUBLISHABLE_KEY ??
        process.env.SUPABASE_ANON_KEY ??
        process.env.VITE_SUPABASE_PUBLISHABLE_KEY;
      if (!url || !key) return FALLBACK.map((r) => ({ ...r, price: "sprawdź ceny" }));

      const sb = createClient(url, key, {
        auth: { persistSession: false, autoRefreshToken: false },
      });

      // Wczytaj cache realnych cen (display_price = real - 50, zaokr. do 9).
      const liveNumeric = new Map<string, number>();
      const { data: priceRows } = await sb
        .from("route_prices")
        .select("from_iata,to_iata,display_price");
      if (priceRows) {
        for (const r of priceRows) {
          if (typeof r.display_price === "number" && r.display_price > 0) {
            liveNumeric.set(`${r.from_iata}-${r.to_iata}`, r.display_price);
          }
        }
      }
      // Rozwiń kod metro (np. WAR = WAW+WMI, LON = LHR+LGW+STN+LTN+LCY) na
      // listę realnych kodów IATA — żeby dla par typu WAR→BCN znaleźć cenę
      // w cache, gdzie trzymamy tylko WAW→BCN / WMI→BCN.
      const expand = (code: string): string[] => {
        const ap = airportByLowerCode(code.toLowerCase());
        if (ap?.isMetro && ap.members && ap.members.length > 0) return ap.members;
        return [code];
      };
      const priceFor = (pair: string): string => {
        const direct = liveNumeric.get(pair);
        if (direct) return formatDisplayPrice(direct);
        const [f, t] = pair.split("-");
        const froms = expand(f);
        const tos = expand(t);
        let min = Infinity;
        for (const ff of froms) {
          for (const tt of tos) {
            const v = liveNumeric.get(`${ff}-${tt}`);
            if (typeof v === "number" && v < min) min = v;
          }
        }
        if (Number.isFinite(min)) return formatDisplayPrice(min);
        // Brak ceny z feedu Aviasales — nie pokazujemy zmyślonej kwoty.
        return "sprawdź ceny";
      };

      // Ostatnie 60 dni — wystarczająca próbka, wciąż "świeże" trasy.
      const since = new Date(Date.now() - 60 * 86_400_000).toISOString();
      const { data, error } = await sb
        .from("search_queries")
        .select("from_iata,to_iata")
        .not("from_iata", "is", null)
        .not("to_iata", "is", null)
        .gte("created_at", since)
        .limit(5000);

      const applyLive = (rows: PopularRoute[]): PopularRoute[] =>
        rows.map((r) => ({ ...r, price: priceFor(`${r.from}-${r.to}`) }));

      if (error || !data) return applyLive(FALLBACK);

      // Agregacja po parze (from→to).
      const counts = new Map<string, number>();
      for (const row of data) {
        const f = (row.from_iata ?? "").toUpperCase();
        const t = (row.to_iata ?? "").toUpperCase();
        if (!f || !t || f === t) continue;
        // „Cały kraj" (XPL, XGB…) — Aviasales tego nie wyszukuje, pomijamy.
        if (isCountryCode(f) || isCountryCode(t)) continue;
        const k = `${f}-${t}`;
        counts.set(k, (counts.get(k) ?? 0) + 1);
      }

      const ranked = [...counts.entries()]
        .filter(([, c]) => c >= MIN_COUNT_PER_ROUTE)
        .sort((a, b) => b[1] - a[1]);

      if (ranked.length < MIN_ROUTES_FROM_DB) return applyLive(FALLBACK);

      const out: PopularRoute[] = [];
      for (const [pair] of ranked) {
        if (out.length >= 12) break;
        const [from, to] = pair.split("-");
        const toAp = airportByLowerCode(to.toLowerCase());
        if (!toAp) continue;
        out.push({
          from,
          to,
          city: toAp.city,
          price: priceFor(pair),
        });
      }

      // Dopełnij fallbackiem, jeśli brakuje do 12 (różne pary).
      if (out.length < 12) {
        const seen = new Set(out.map((r) => `${r.from}-${r.to}`));
        for (const f of FALLBACK) {
          if (out.length >= 12) break;
          if (!seen.has(`${f.from}-${f.to}`)) {
            out.push({ ...f, price: priceFor(`${f.from}-${f.to}`) });
          }
        }
      }

      return out;
    } catch {
      return FALLBACK.map((r) => ({ ...r, price: "sprawdź ceny" }));
    }
  },
);

